import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import styles from './ContenidosPage.module.css';

const fileIcon = (tipo, ruta) => {
  if (tipo === 'enlace') return '🔗';
  if (tipo === 'texto')  return '📝';
  if (!ruta) return '📄';
  const ext = ruta.split('.').pop().toLowerCase();
  const icons = { pdf: '📕', doc: '📘', docx: '📘', ppt: '📙', pptx: '📙',
                  xls: '📗', xlsx: '📗', txt: '📄', png: '🖼️', jpg: '🖼️',
                  jpeg: '🖼️', gif: '🖼️', zip: '🗜️', mp4: '🎬', mp3: '🎵' };
  return icons[ext] || '📄';
};

const isPrevisualizble = (ruta) => {
  if (!ruta) return false;
  const ext = ruta.split('.').pop().toLowerCase();
  return ['pdf','png','jpg','jpeg','gif','txt','mp4','mp3'].includes(ext);
};

export default function ContenidosPage() {
  const { id: asigId } = useParams();
  const { user } = useAuth();
  const isProfesor = user?.rol === 'profesor';

  const [carpetas, setCarpetas]                   = useState([]);
  const [raiz, setRaiz]                           = useState([]);
  const [carpetaActual, setCarpetaActual]         = useState(null);
  const [contenidosCarpeta, setContenidosCarpeta] = useState([]);
  const [loading, setLoading]                     = useState(true);
  const [preview, setPreview]                     = useState(null);
  const [showAddContenido, setShowAddContenido]   = useState(false);
  const [showAddCarpeta, setShowAddCarpeta]       = useState(false);
  const [formCont, setFormCont] = useState({ titulo: '', descripcion: '', tipo: 'archivo', url_enlace: '', contenido_texto: '' });
  const [formCarp, setFormCarp] = useState({ nombre: '', descripcion: '' });
  const [archivo, setArchivo]   = useState(null);
  const [saving, setSaving]     = useState(false);
  const [msg, setMsg]           = useState('');

  const cargar = () => {
    setLoading(true);
    api.get(`/asignaturas/${asigId}/contenidos`)
      .then(r => {
        setCarpetas(r.data.data.carpetas || []);
        setRaiz(r.data.data.raiz || []);
      })
      .finally(() => setLoading(false));
  };

  const cargarCarpeta = (carpeta) => {
    setCarpetaActual(carpeta);
    api.get(`/carpetas/${carpeta.id}/contenidos`)
      .then(r => setContenidosCarpeta(r.data.data));
  };

  useEffect(() => { cargar(); }, [asigId]);

  const cerrarPreview = () => {
    if (preview?.blobUrl) URL.revokeObjectURL(preview.blobUrl);
    setPreview(null);
  };

  const abrirPreview = async (cont) => {
    if (cont.tipo === 'texto') {
      setPreview({ ...cont, blobUrl: null, loadingBlob: false });
      return;
    }
    setPreview({ ...cont, blobUrl: null, loadingBlob: true });
    const urlId = cont.slug || cont.id;
    try {
      const response = await api.get(`/contenidos/${urlId}/preview`, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(response.data);
      setPreview({ ...cont, blobUrl, loadingBlob: false });
    } catch {
      setPreview({ ...cont, blobUrl: null, loadingBlob: false, error: true });
    }
  };

  const descargarArchivo = async (cont) => {
    try {
      const urlId = cont.slug || cont.id;
      const response = await api.get(`/contenidos/${urlId}/descargar`, { responseType: 'blob' });
      const ext = cont.ruta_archivo?.split('.').pop().toLowerCase() || '';
      const nombre = cont.titulo + (ext ? '.' + ext : '');
      const url = URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url; a.download = nombre;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { alert('Error al descargar'); }
  };

  // Toggle visible/oculto (0 o 1)
  const toggleVisible = async (e, cont) => {
    e.stopPropagation();
    const nuevoVisible = parseInt(cont.visible) === 1 ? 0 : 1;
    try {
      await api.post(`/contenidos/${cont.id}`, { _method: 'PUT', visible: nuevoVisible });
      if (carpetaActual) cargarCarpeta(carpetaActual); else cargar();
    } catch { alert('Error al cambiar visibilidad'); }
  };

  // Eliminar real (visible=-1)
  const eliminarContenido = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este contenido? Desaparecerá para todos, incluido el profesor.')) return;
    try {
      await api.post(`/contenidos/${id}`, { _method: 'DELETE' });
      if (carpetaActual) cargarCarpeta(carpetaActual); else cargar();
    } catch { alert('Error al eliminar'); }
  };

  const subirContenido = async e => {
    e.preventDefault(); setSaving(true); setMsg('');
    try {
      const fd = new FormData();
      Object.entries(formCont).forEach(([k, v]) => fd.append(k, v));
      if (carpetaActual) fd.append('carpeta_id', carpetaActual.id);
      if (archivo) fd.append('archivo', archivo);
      await api.post(`/asignaturas/${asigId}/contenidos`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setMsg('✓ Contenido añadido');
      setShowAddContenido(false);
      setFormCont({ titulo: '', descripcion: '', tipo: 'archivo', url_enlace: '', contenido_texto: '' });
      setArchivo(null);
      if (carpetaActual) cargarCarpeta(carpetaActual); else cargar();
    } catch (err) {
      setMsg(err.response?.data?.error || 'Error al subir');
    } finally { setSaving(false); }
  };

  const crearCarpeta = async e => {
    e.preventDefault();
    await api.post(`/asignaturas/${asigId}/carpetas`, formCarp);
    setShowAddCarpeta(false);
    setFormCarp({ nombre: '', descripcion: '' });
    cargar();
  };

  // Toggle visible/oculto carpeta
  const toggleCarpeta = async (e, carp) => {
    e.stopPropagation();
    const nuevoVisible = parseInt(carp.visible) === 1 ? 0 : 1;
    try {
      await api.post(`/carpetas/${carp.id}`, { _method: 'PUT', visible: nuevoVisible });
      if (carpetaActual?.id === carp.id) setCarpetaActual(null);
      cargar();
    } catch { alert('Error al cambiar visibilidad'); }
  };

  // Eliminar carpeta real
  const eliminarCarpeta = async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar esta carpeta y todo su contenido? Desaparecerá para todos.')) return;
    try {
      await api.post(`/carpetas/${id}`, { _method: 'DELETE' });
      if (carpetaActual?.id === id) setCarpetaActual(null);
      cargar();
    } catch { alert('Error al eliminar carpeta'); }
  };

  const renderContenido = (cont) => {
    const oculto = parseInt(cont.visible) === 0;
    return (
      <div key={`cont-${cont.id}`}
        className={`${styles.contItem} ${oculto ? styles.contItemOculto : ''}`}>
        <span className={styles.contIcon}>{fileIcon(cont.tipo, cont.ruta_archivo)}</span>
        <div className={styles.contInfo}>
          <div className={styles.contTituloRow}>
            <p className={styles.contTitulo}>{cont.titulo}</p>
            {oculto && isProfesor && (
              <span className={styles.badgeOculto}>🙈 Oculto para alumnos</span>
            )}
          </div>
          {cont.descripcion && <p className={styles.contDesc}>{cont.descripcion}</p>}
          {cont.tipo === 'enlace' && (
            <a href={cont.url_enlace} target="_blank" rel="noreferrer" className={styles.enlaceUrl}>
              {cont.url_enlace}
            </a>
          )}
          {cont.tipo === 'texto' && (
            <p className={styles.textoPreview}>{cont.contenido_texto?.slice(0, 120)}...</p>
          )}
        </div>
        <div className={styles.contActions}>
          {cont.tipo === 'archivo' && isPrevisualizble(cont.ruta_archivo) && (
            <button className={styles.btnVer} onClick={() => abrirPreview(cont)}>👁 Ver</button>
          )}
          {cont.tipo === 'archivo' && (
            <button className={styles.btnDescargar} onClick={() => descargarArchivo(cont)}>⬇ Descargar</button>
          )}
          {cont.tipo === 'texto' && (
            <button className={styles.btnVer} onClick={() => abrirPreview(cont)}>👁 Ver</button>
          )}
          {isProfesor && (
            <>
              <button
                className={oculto ? styles.btnMostrar : styles.btnOcultar}
                onClick={e => toggleVisible(e, cont)}
                title={oculto ? 'Hacer visible para alumnos' : 'Ocultar a los alumnos'}>
                {oculto ? '👁 Mostrar' : '🙈 Ocultar'}
              </button>
              <button className={styles.btnEliminar}
                onClick={e => eliminarContenido(e, cont.id)}
                title="Eliminar para todos">
                🗑
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.page}>
      {carpetaActual && (
        <div className={styles.breadcrumb}>
          <button className={styles.crumb} onClick={() => setCarpetaActual(null)}>
            ← Volver a contenidos
          </button>
          <span className={styles.sep}>›</span>
          <span className={`${styles.crumb} ${styles.crumbActive}`}>
            📁 {carpetaActual.nombre}
          </span>
        </div>
      )}

      {isProfesor && (
        <div className={styles.actions}>
          <button className={styles.btnNew}
            onClick={() => { setShowAddCarpeta(p => !p); setShowAddContenido(false); }}>
            {showAddCarpeta ? '✕ Cancelar' : '📁 Nueva carpeta'}
          </button>
          <button className={styles.btnNew}
            onClick={() => { setShowAddContenido(p => !p); setShowAddCarpeta(false); }}>
            {showAddContenido ? '✕ Cancelar' : '+ Añadir contenido'}
          </button>
        </div>
      )}

      {showAddCarpeta && (
        <form onSubmit={crearCarpeta} className={styles.form}>
          <h4 className={styles.formTitle}>Nueva carpeta</h4>
          <input className={styles.input} placeholder="Nombre (ej: Tema 01)"
            value={formCarp.nombre}
            onChange={e => setFormCarp(p => ({ ...p, nombre: e.target.value }))} required />
          <input className={styles.input} placeholder="Descripción (opcional)"
            value={formCarp.descripcion}
            onChange={e => setFormCont(p => ({ ...p, descripcion: e.target.value }))} />
          <button type="submit" className={styles.btnSubmit}>Crear carpeta</button>
        </form>
      )}

      {showAddContenido && (
        <form onSubmit={subirContenido} className={styles.form}>
          <h4 className={styles.formTitle}>
            Añadir en {carpetaActual ? `"${carpetaActual.nombre}"` : 'contenidos'}
          </h4>
          <input className={styles.input} placeholder="Título"
            value={formCont.titulo}
            onChange={e => setFormCont(p => ({ ...p, titulo: e.target.value }))} required />
          <input className={styles.input} placeholder="Descripción (opcional)"
            value={formCont.descripcion}
            onChange={e => setFormCont(p => ({ ...p, descripcion: e.target.value }))} />
          <div className={styles.tipoSelector}>
            {['archivo','enlace','texto'].map(t => (
              <button key={t} type="button"
                className={`${styles.tipoBtn} ${formCont.tipo === t ? styles.tipoActivo : ''}`}
                onClick={() => setFormCont(p => ({ ...p, tipo: t }))}>
                {t === 'archivo' ? '📄 Archivo' : t === 'enlace' ? '🔗 Enlace' : '📝 Texto'}
              </button>
            ))}
          </div>
          {formCont.tipo === 'archivo' && (
            <input type="file"
              accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.png,.jpg,.jpeg,.gif,.zip,.mp4,.mp3"
              onChange={e => setArchivo(e.target.files[0])} required />
          )}
          {formCont.tipo === 'enlace' && (
            <input className={styles.input} placeholder="https://..."
              value={formCont.url_enlace}
              onChange={e => setFormCont(p => ({ ...p, url_enlace: e.target.value }))} required />
          )}
          {formCont.tipo === 'texto' && (
            <textarea className={styles.textarea} rows="5" placeholder="Contenido..."
              value={formCont.contenido_texto}
              onChange={e => setFormCont(p => ({ ...p, contenido_texto: e.target.value }))} required />
          )}
          {msg && <p className={msg.startsWith('✓') ? styles.success : styles.error}>{msg}</p>}
          <button type="submit" className={styles.btnSubmit} disabled={saving}>
            {saving ? 'Subiendo...' : 'Guardar'}
          </button>
        </form>
      )}

      {loading && <p className={styles.empty}>Cargando contenidos...</p>}

      {!loading && (
        <div className={styles.contenidosWrap}>
          {carpetaActual === null && carpetas.length === 0 && raiz.length === 0 && (
            <div className={styles.emptyState}>
              <p className={styles.emptyIcon}>📂</p>
              <p className={styles.emptyText}>No hay contenidos publicados todavía.</p>
            </div>
          )}

          {carpetaActual === null && (carpetas.length > 0 || raiz.length > 0) && (
            <div className={styles.contList}>
              {carpetas.map(carp => {
                const carpOculta = parseInt(carp.visible) === 0;
                return (
                  <div key={`carp-${carp.id}`}
                    className={`${styles.carpetaItem} ${carpOculta ? styles.carpetaOculta : ''}`}
                    onClick={() => cargarCarpeta(carp)}>
                    <span className={styles.contIcon}>📁</span>
                    <div className={styles.contInfo}>
                      <div className={styles.contTituloRow}>
                        <p className={styles.contTitulo}>{carp.nombre}</p>
                        {carpOculta && isProfesor && (
                          <span className={styles.badgeOculto}>🙈 Oculto para alumnos</span>
                        )}
                      </div>
                      {carp.descripcion && <p className={styles.contDesc}>{carp.descripcion}</p>}
                      <p className={styles.carpetaCount}>{carp.total_contenidos} elemento(s)</p>
                    </div>
                    {isProfesor && (
                      <div className={styles.carpetaActions} onClick={e => e.stopPropagation()}>
                        <button
                          className={carpOculta ? styles.btnMostrar : styles.btnOcultar}
                          onClick={e => toggleCarpeta(e, carp)}>
                          {carpOculta ? '👁 Mostrar' : '🙈 Ocultar'}
                        </button>
                        <button className={styles.btnElimCarp}
                          onClick={e => eliminarCarpeta(e, carp.id)}>
                          🗑
                        </button>
                      </div>
                    )}
                    <span className={styles.carpetaArrow}>›</span>
                  </div>
                );
              })}
              {raiz.map(renderContenido)}
            </div>
          )}

          {carpetaActual !== null && contenidosCarpeta.length === 0 && (
            <div className={styles.emptyState}>
              <p className={styles.emptyIcon}>📁</p>
              <p className={styles.emptyText}>Esta carpeta está vacía.</p>
            </div>
          )}

          {carpetaActual !== null && contenidosCarpeta.length > 0 && (
            <div className={styles.contList}>
              {contenidosCarpeta.map(renderContenido)}
            </div>
          )}
        </div>
      )}

      {preview && (
        <div className={styles.overlay} onClick={cerrarPreview}>
          <div className={styles.previewBox} onClick={e => e.stopPropagation()}>
            <div className={styles.previewHeader}>
              <h3 className={styles.previewTitle}>{preview.titulo}</h3>
              <div className={styles.previewHeaderActions}>
                {preview.tipo === 'archivo' && (
                  <button className={styles.btnDescPrev} onClick={() => descargarArchivo(preview)}>
                    ⬇ Descargar
                  </button>
                )}
                <button className={styles.btnCerrar} onClick={cerrarPreview}>✕</button>
              </div>
            </div>
            <div className={styles.previewContent}>
              {preview.tipo === 'texto' && (
                <pre className={styles.textoContent}>{preview.contenido_texto}</pre>
              )}
              {preview.tipo === 'archivo' && (() => {
                if (preview.loadingBlob) return <div className={styles.loadingPrev}><p>Cargando...</p></div>;
                if (preview.error) return <p className={styles.noPrev}>❌ Error al cargar el archivo.</p>;
                const url = preview.blobUrl;
                const ext = preview.ruta_archivo?.split('.').pop().toLowerCase();
                if (ext === 'pdf') return <iframe src={url} className={styles.iframePrev} title={preview.titulo} />;
                if (['png','jpg','jpeg','gif'].includes(ext)) return <img src={url} alt={preview.titulo} className={styles.imgPrev} />;
                if (ext === 'mp4') return <video src={url} controls className={styles.videoPrev} />;
                if (ext === 'mp3') return <audio src={url} controls className={styles.audioPrev} />;
                if (ext === 'txt') return <iframe src={url} className={styles.iframePrev} title={preview.titulo} />;
                return <p className={styles.noPrev}>Sin previsualización disponible.</p>;
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
